/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  Box,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormHelperText,
  Chip,
  Typography,
  Paper,
  Button,
  IconButton,
} from '@mui/material';
import { StyledDropzone } from './Dropzone';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import { ReportTagModel } from '../../Pages/ReportTag';
import { apiClient } from '../../helper/api';
import useAuth from '../../context/AuthProvider';
import Swal from 'sweetalert2';
import { GreenBtnColor, OrangeBtnColor, PrimaryBlueColor } from '../../helper/theme';

const sectionTitleSx = {
  color: 'text.secondary',
  fontWeight: 600,
  letterSpacing: '0.08em',
  display: 'block',
  mb: 1,
};
const paperSectionSx = {
  p: 2,
  borderRadius: 2,
  borderColor: 'divider',
  backgroundColor: 'grey.50',
};

export interface UploadReportTagDocumentsProps {
    uploadDocumentsOpen: boolean;
    handleCloseUploadDocuments: () => void;
    reportTag: ReportTagModel;
    /** When true (client + in-house close), block uploads and resolve-in-house actions. */
    readOnlyTag?: boolean;
    clientDocumentData: { reportTagId: number, files: File[]; inboxId?: number };
    actionFunction: () => void;
};
export const UploadReportTagDocuments: React.FC<UploadReportTagDocumentsProps> = ({ 
    uploadDocumentsOpen, 
    handleCloseUploadDocuments, 
    reportTag, 
    readOnlyTag = false,
    clientDocumentData, 
    actionFunction
}) => {
    const { user } = useAuth();
    const token = user?.token;
    const [loading, setLoading] = useState(false);
    const [uploadedDocuments, setUploadedDocuments] = useState<File[]>([]);

    const handleUploadDocuments = (files: File[]) => {
        if (readOnlyTag) return;
        setUploadedDocuments(files);
        const isResolvingInHouse = files.length === 0;
        const formData = new FormData();
        if (!isResolvingInHouse) {
            files.forEach((file) => {
                formData.append('files', file);
            });
        }
        formData.append('data', JSON.stringify({
            reportTagId: clientDocumentData.reportTagId,
            isResolvingInHouse: isResolvingInHouse,
            inboxId: clientDocumentData.inboxId,
        }));
        Swal.fire({
            title: '<div style="color: white;">Are you sure?</div>',
            html: `<div style="color: white;">${isResolvingInHouse ? 'This will notify MISC that you wish to handle the tag in house and we will close this request.' : 'This will send the documents to MISC'}</div>`,
            // text: isResolvingInHouse ? 'This will notify MISC that you wish to handle the tag in house and we will close this request.' : 'This will send the documents to MISC',
            showCancelButton: true,
            confirmButtonText: isResolvingInHouse ? 'Yes, resolve it!' : 'Yes, send it!',
            cancelButtonText: 'No, Continue to UPLOAD',
            confirmButtonColor: GreenBtnColor,
            cancelButtonColor: OrangeBtnColor,
            reverseButtons: true,
            background: PrimaryBlueColor,
        }).then((result) => {
            if (result.isConfirmed) {
                setLoading(true);
                apiClient('/ReportTags/sendTagToMisc', {
                    method: 'POST',
                    headers: new Headers({
                        'Authorization': `Bearer ${token}`,
                    }),
                    body: formData,
                }).then((response) => {
                    if (!response) return;
                    Swal.fire({
                        title: isResolvingInHouse ? 'Tag Resolved' : 'Documents Sent to MISC',
                        text: isResolvingInHouse ? 'Tag resolved successfully' : 'Documents sent to MISC successfully',
                        icon: 'success',
                        confirmButtonText: 'OK',
                    });
                    setUploadedDocuments([]);
                    actionFunction();
                    handleCloseUploadDocuments();
                }).catch((error) => {
                    console.error("Error sending documents to MISC:", error);
                    Swal.fire({
                        title: 'Error',
                        text: 'Failed to send documents to MISC',
                        icon: 'error',
                        confirmButtonText: 'OK',
                    });
                }).finally(() => {
                    setLoading(false);
                });
            }
        });
    };

    useEffect(() => {
        if (!uploadDocumentsOpen) {
            setUploadedDocuments([]);
        }
    }, [uploadDocumentsOpen]);

    return (
        <Dialog
            open={uploadDocumentsOpen}
            onClose={handleCloseUploadDocuments}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    width: '90%',
                    maxWidth: 900,
                    minHeight: '40vh',
                    maxHeight: '90vh',
                    borderRadius: 2,
                    boxShadow: '0 24px 48px rgba(0,0,0,0.12)',
                    overflow: 'hidden',
                },
            }}
            sx={{ '& .MuiDialog-paper': { overflow: 'hidden' } }}
        >
            {loading && <LinearProgress sx={{ flexShrink: 0 }} />}
            <DialogTitle
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    py: 2,
                    px: 3,
                    background: 'linear-gradient(135deg, #0d4a63 0%, #15607a 100%)',
                    color: '#fff',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CloudUploadOutlinedIcon sx={{ fontSize: 28, opacity: 0.95 }} />
                    <Typography component="span" variant="h6" sx={{ fontWeight: 600, letterSpacing: '0.02em' }}>
                        Upload Documents to resolve the issue
                    </Typography>
                </Box>
                <IconButton
                    onClick={handleCloseUploadDocuments}
                    sx={{
                        color: 'rgba(255,255,255,0.9)',
                        '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.12)' },
                    }}
                    aria-label="Close dialog"
                >
                    <CloseOutlinedIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ px: 3, py: 2, mt: 2, overflow: 'auto' }}>
                <Typography variant="overline" sx={sectionTitleSx}>
                    Upload documents
                </Typography>
                <Paper variant="outlined" sx={{ ...paperSectionSx, ...(readOnlyTag ? { opacity: 0.6, pointerEvents: 'none' } : {}) }}>
                    <FormHelperText className="formtexthelper-heading" sx={{ mt: 0, mb: 1 }}>
                        Select File(s) or Drag and Drop
                    </FormHelperText>
                    <StyledDropzone
                        label="Select File(s) or Drag and Drop"
                        onFileDrop={(files: File[]) => {
                            if (readOnlyTag) return;
                            setUploadedDocuments([...uploadedDocuments, ...files]);
                        }}
                    />
                    {uploadedDocuments.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <FormHelperText className="formtexthelper-heading" sx={{ mb: 1 }}>
                                Selected Files ({uploadedDocuments.length})
                            </FormHelperText>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {uploadedDocuments.map((file, index) => (
                                    <Chip
                                        key={index}
                                        label={file.name}
                                        onDelete={() => {
                                            setUploadedDocuments(uploadedDocuments.filter((_, i) => i !== index));
                                        }}
                                        deleteIcon={<CancelIcon />}
                                        color="primary"
                                        variant="outlined"
                                        sx={{ fontWeight: 500 }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}
                </Paper>
            </DialogContent>
            <DialogActions
                sx={{
                    borderTop: 1,
                    borderColor: 'divider',
                    backgroundColor: 'grey.50',
                    px: 3,
                    py: 2,
                    gap: 1.5,
                    flexWrap: 'wrap',
                }}
            >
                <Button
                    variant="outlined"
                    startIcon={<CloseOutlinedIcon />}
                    onClick={handleCloseUploadDocuments}
                    sx={{ fontWeight: 600, textTransform: 'none' }}
                >
                    Close
                </Button>
                <Button
                    variant="contained"
                    startIcon={<SendOutlinedIcon />}
                    onClick={() => handleUploadDocuments(uploadedDocuments)}
                    disabled={readOnlyTag || uploadedDocuments.length === 0}
                    sx={{
                        backgroundColor: '#2099d2',
                        '&:hover': { backgroundColor: '#1a7ba8' },
                        fontWeight: 600,
                        textTransform: 'none',
                    }}
                >
                    Send to MISC
                </Button>
            </DialogActions>
        </Dialog>
    );
};